// M13 Slice 5c — Sektör Paketi Upgrade Modal.
// Outdated paket için yeni parent version seçimi + dry-run preview + onay.
// Backend: POST /admin/sector-packages/:id/upgrade { newParentFrameworkVersionId, dryRun }

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type {
  FrameworkDetail,
  SectorPackageOrphanReport,
  UpgradeSectorPackageResponse,
} from "@mahir/shared";
import { getFramework } from "../../api/frameworks";
import { upgradeSectorPackage } from "../../api/sector-packages";

interface Props {
  packageId: string;
  parentFrameworkId: string;
  currentParentVersionId: string;
  currentParentVersionNumber: string;
  onClose: () => void;
  onUpgraded: () => void;
}

export function UpgradeModal({
  packageId,
  parentFrameworkId,
  currentParentVersionId,
  currentParentVersionNumber,
  onClose,
  onUpgraded,
}: Props): JSX.Element {
  const [framework, setFramework] = useState<FrameworkDetail | null>(null);
  const [targetVersionId, setTargetVersionId] = useState<string>("");
  const [preview, setPreview] = useState<UpgradeSectorPackageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<"preview" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFramework(parentFrameworkId)
      .then((fw) => {
        setFramework(fw);
        // Eligible target: published + != currentParentVersionId
        const eligible = fw.versions.filter(
          (v) => v.status === "published" && v.id !== currentParentVersionId,
        );
        if (eligible.length > 0) {
          setTargetVersionId(eligible[0]!.id);
        }
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [parentFrameworkId, currentParentVersionId]);

  const eligibleTargets = framework
    ? framework.versions.filter((v) => v.status === "published" && v.id !== currentParentVersionId)
    : [];

  const handlePreview = async (): Promise<void> => {
    if (!targetVersionId) return;
    setBusyAction("preview");
    setError(null);
    try {
      const res = await upgradeSectorPackage(packageId, {
        newParentFrameworkVersionId: targetVersionId,
        dryRun: true,
      });
      setPreview(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Önizleme başarısız");
    } finally {
      setBusyAction(null);
    }
  };

  const handleApply = async (): Promise<void> => {
    if (!targetVersionId) return;
    setBusyAction("apply");
    setError(null);
    try {
      await upgradeSectorPackage(packageId, {
        newParentFrameworkVersionId: targetVersionId,
        dryRun: false,
      });
      onUpgraded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yükseltme başarısız");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface)",
          borderRadius: 12,
          padding: 24,
          width: 560,
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem" }}>Paketi Yükselt</h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--color-text-2)",
            }}
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <p className="text-sm mb-3" style={{ color: "var(--color-text-2)" }}>
          Mevcut üst sürüm: <strong>{currentParentVersionNumber}</strong>. Code-based remap
          uygulanır; üst sürümde olmayan exclusion/override referansları (orphan) drop edilir.
        </p>

        {loading ? (
          <div style={{ color: "var(--color-text-3)" }}>Yükleniyor…</div>
        ) : eligibleTargets.length === 0 ? (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              background: "color-mix(in srgb, var(--color-warning, #f0ad4e) 8%, white)",
              border: "1px solid var(--color-warning, #f0ad4e)",
              fontSize: "0.9rem",
            }}
          >
            Yükseltilebilir başka bir yayında sürüm yok.
          </div>
        ) : (
          <>
            <label
              style={{
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              Hedef Sürüm
            </label>
            <select
              value={targetVersionId}
              onChange={(e) => {
                setTargetVersionId(e.target.value);
                setPreview(null);
              }}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                fontSize: "0.9rem",
                marginBottom: 16,
              }}
            >
              {eligibleTargets.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.versionNumber}{" "}
                  {v.publishedAt ? `(${new Date(v.publishedAt).toLocaleDateString("tr-TR")})` : ""}
                </option>
              ))}
            </select>

            {preview && <OrphanReport report={preview.orphans} />}

            {error && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: "color-mix(in srgb, var(--color-error) 10%, white)",
                  border: "1px solid var(--color-error)",
                  color: "var(--color-error)",
                  fontSize: "0.85rem",
                  marginBottom: 12,
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                paddingTop: 12,
                borderTop: "1px solid var(--color-border)",
              }}
            >
              <button
                onClick={onClose}
                disabled={busyAction !== null}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--color-border)",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "0.88rem",
                }}
              >
                İptal
              </button>
              <button
                onClick={() => void handlePreview()}
                disabled={busyAction !== null}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "1px solid var(--color-primary)",
                  background: "transparent",
                  color: "var(--color-primary)",
                  cursor: busyAction !== null ? "not-allowed" : "pointer",
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  opacity: busyAction !== null ? 0.5 : 1,
                }}
              >
                {busyAction === "preview" ? "Önizleniyor…" : "Önizle (dryRun)"}
              </button>
              <button
                onClick={() => {
                  if (!preview) {
                    const ok = window.confirm(
                      "Önizleme yapmadan upgrade uygulanacak. Devam edilsin mi?",
                    );
                    if (!ok) return;
                  }
                  void handleApply();
                }}
                disabled={busyAction !== null || !targetVersionId}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "none",
                  background: "var(--color-primary)",
                  color: "white",
                  cursor: busyAction !== null ? "not-allowed" : "pointer",
                  fontSize: "0.88rem",
                  fontWeight: 600,
                  opacity: busyAction !== null ? 0.5 : 1,
                }}
              >
                {busyAction === "apply" ? "Uygulanıyor…" : "Uygula"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OrphanReport({ report }: { report: SectorPackageOrphanReport }): JSX.Element {
  const total =
    report.excludedSectionCodes.length +
    report.excludedQuestionCodes.length +
    report.sectionOverrideKeys.length +
    report.questionOverrideKeys.length;

  if (total === 0) {
    return (
      <div
        style={{
          padding: 12,
          borderRadius: 8,
          background: "color-mix(in srgb, var(--color-success, #2f7a3e) 8%, white)",
          border: "1px solid var(--color-success, #2f7a3e)",
          color: "var(--color-success, #2f7a3e)",
          fontSize: "0.88rem",
          marginBottom: 12,
        }}
      >
        ✓ Bu sürüme upgrade temiz — drop edilecek referans yok.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 8,
        background: "color-mix(in srgb, var(--color-warning, #f0ad4e) 10%, white)",
        border: "1px solid var(--color-warning, #f0ad4e)",
        fontSize: "0.85rem",
        marginBottom: 12,
      }}
    >
      <strong>⚠ {total.toString()} orphan referans drop edilecek:</strong>
      <ul style={{ margin: "8px 0 0 16px", padding: 0, lineHeight: 1.7 }}>
        {report.excludedSectionCodes.length > 0 && (
          <li>
            <strong>Bölüm exclude:</strong> <code>{report.excludedSectionCodes.join(", ")}</code>
          </li>
        )}
        {report.excludedQuestionCodes.length > 0 && (
          <li>
            <strong>Soru exclude:</strong> <code>{report.excludedQuestionCodes.join(", ")}</code>
          </li>
        )}
        {report.sectionOverrideKeys.length > 0 && (
          <li>
            <strong>Bölüm override:</strong> <code>{report.sectionOverrideKeys.join(", ")}</code>
          </li>
        )}
        {report.questionOverrideKeys.length > 0 && (
          <li>
            <strong>Soru override:</strong> <code>{report.questionOverrideKeys.join(", ")}</code>
          </li>
        )}
      </ul>
    </div>
  );
}
